import React, { useState, useEffect, useMemo } from 'react';

/*
 * PUBLIC, no-login lead-magnet tool: /tools/rate-card-generator
 * A creator enters their stats -> gets suggested brand-deal pricing + a clean,
 * shareable rate card they can print/save as PDF. Funnels to CreatorKhata signup.
 * Fully client-side (no backend, no auth). SEO title/desc set on mount.
 */

const ACCENT = '#1a7a4a';
const INK = '#0f1117';
const SIGNUP_URL = '/?auth=signup';

// Suggested ₹ per view for a YouTube integration, by niche.
// Calibrated to 2025 India branded-content CPM benchmarks: ₹1,265–₹2,109 per
// 1,000 views (= ₹1.27–₹2.11/view), with niche premium for finance/tech
// (rates swing 300–400% across niches). Sources: BossWallah, Kalakaaar, Base2Brand.
const NICHE_RATE = {
  'Finance / Business': 2.1,
  'Tech / Gadgets': 1.9,
  'Education': 1.6,
  'Beauty / Fashion': 1.5,
  'Fitness / Health': 1.4,
  'Food / Cooking': 1.3,
  'Travel': 1.4,
  'Lifestyle / Vlog': 1.3,
  'Gaming': 1.1,
  'Comedy / Entertainment': 1.1,
  'Other': 1.3,
};
const NICHES = Object.keys(NICHE_RATE);

const roundTo = (x, step) => Math.max(step, Math.round(x / step) * step);
const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

function priceRange(mid) {
  if (!mid || mid <= 0) return null;
  const step = mid >= 20000 ? 1000 : 500;
  return { low: roundTo(mid * 0.8, step), high: roundTo(mid * 1.25, step) };
}

export default function RateCardToolPage() {
  const [name, setName] = useState('');
  const [niche, setNiche] = useState(NICHES[0]);
  const [platform, setPlatform] = useState('YouTube');
  const [followers, setFollowers] = useState('');
  const [avgViews, setAvgViews] = useState('');

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Free Creator Rate Card Generator (India) — CreatorKhata';
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta'); m.name = 'description';
      document.head.appendChild(m); return m;
    })();
    const prevDesc = meta.getAttribute('content');
    meta.setAttribute('content',
      'Free rate card generator for Indian YouTubers & Instagram creators. Get suggested brand-deal pricing based on your views and niche, then download a professional rate card to send to brands.');
    return () => { document.title = prevTitle; if (prevDesc) meta.setAttribute('content', prevDesc); };
  }, []);

  const rows = useMemo(() => {
    const views = parseInt(avgViews, 10) || 0;
    const base = views * (NICHE_RATE[niche] || 1.3); // integration value
    if (!base) return [];
    const items = platform === 'YouTube'
      ? [
          ['Dedicated Video', 2.5, 'Full video about the brand'],
          ['Integration (60–90s)', 1.0, 'Sponsored segment in a regular video'],
          ['YouTube Short', 0.4, 'Dedicated short-form video'],
          ['Community Post', 0.15, 'Image/text post'],
        ]
      : [
          ['Dedicated Reel', 1.0, 'Full reel about the brand'],
          ['Reel Integration', 0.6, 'Brand mention in a regular reel'],
          ['Static Post / Carousel', 0.5, 'Feed post'],
          ['Story (per frame)', 0.12, 'Single story frame'],
        ];
    return items
      .map(([label, mult, hint]) => ({ label, hint, range: priceRange(base * mult) }))
      .filter(r => r.range);
  }, [avgViews, niche, platform]);

  const packages = useMemo(() => {
    const views = parseInt(avgViews, 10) || 0;
    const base = views * (NICHE_RATE[niche] || 1.3);
    if (!base) return [];
    const starterMid = base * (platform === 'YouTube' ? 1.0 + 0.4 : 1.0 + 0.12) * 0.9;
    const premiumMid = base * (platform === 'YouTube' ? 2.5 + 0.4 + 0.15 : 1.0 + 0.6 + 0.5) * 0.88;
    return [
      { name: 'Starter Bundle', price: roundTo(starterMid, 500),
        desc: platform === 'YouTube' ? '1 Integration + 1 Short' : '1 Reel + 2 Stories' },
      { name: 'Premium Bundle', price: roundTo(premiumMid, 1000),
        desc: platform === 'YouTube' ? '1 Dedicated Video + 1 Short + 1 Community Post' : '1 Reel + 1 Static Post + 3 Stories' },
    ];
  }, [avgViews, niche, platform]);

  const hasData = rows.length > 0;

  const cardHtml = () => {
    const today = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long' });
    const rowsHtml = rows.map(r =>
      `<tr><td style="padding:10px 8px;border-bottom:1px solid #eee"><b>${r.label}</b><div style="font-size:12px;color:#888">${r.hint}</div></td>
       <td style="padding:10px 8px;border-bottom:1px solid #eee;text-align:right;white-space:nowrap;font-weight:700;color:${ACCENT}">${fmt(r.range.low)} – ${fmt(r.range.high)}</td></tr>`).join('');
    const pkgHtml = packages.map(p =>
      `<tr><td style="padding:10px 8px;border-bottom:1px solid #eee"><b>${p.name}</b><div style="font-size:12px;color:#888">${p.desc}</div></td>
       <td style="padding:10px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:700;color:${ACCENT}">${fmt(p.price)}</td></tr>`).join('');
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rate Card — ${name || 'Creator'}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:${INK};max-width:640px;margin:24px auto;padding:0 16px}
      h1{font-size:24px;margin:0}.muted{color:#888;font-size:13px}table{width:100%;border-collapse:collapse;margin-top:8px}
      .head{border-bottom:3px solid ${ACCENT};padding-bottom:12px;margin-bottom:8px}
      .foot{margin-top:18px;font-size:11px;color:#aaa;text-align:center}@media print{.noprint{display:none}}</style></head>
      <body><div class="head"><h1>${name || 'Creator'} — Rate Card</h1>
      <div class="muted">${niche} • ${platform} • ${followers ? Number(followers).toLocaleString('en-IN') + ' followers' : ''} • ${today}</div></div>
      <table>${rowsHtml}</table>
      ${packages.length ? `<h3 style="margin:18px 0 0;font-size:15px">Packages</h3><table>${pkgHtml}</table>` : ''}
      <p class="muted" style="margin-top:14px">All rates are indicative and exclusive of 18% GST. 50% advance to confirm. Final pricing depends on scope, exclusivity, and usage rights.</p>
      <div class="foot">Made with CreatorKhata — creatorkhata.com</div>
      <button class="noprint" onclick="window.print()" style="margin-top:16px;background:${ACCENT};color:#fff;border:none;border-radius:8px;padding:10px 18px;font-weight:700;cursor:pointer">Print / Save as PDF</button>
      </body></html>`;
  };

  const downloadCard = () => {
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow pop-ups to download your rate card.'); return; }
    w.document.write(cardHtml());
    w.document.close();
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fb', fontFamily: "'DM Sans', sans-serif", color: INK }}>
      {/* Header */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', maxWidth: 1080, margin: '0 auto' }}>
        <a href="/" style={{ textDecoration: 'none', color: INK, fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.15rem' }}>
          <span style={{ color: ACCENT }}>●</span> CreatorKhata
        </a>
        <a href={SIGNUP_URL} style={{ ...btn(ACCENT), textDecoration: 'none' }}>Try CreatorKhata free →</a>
      </header>

      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '1.5rem 1.25rem 0.5rem', maxWidth: 720, margin: '0 auto' }}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '2rem', letterSpacing: '-0.02em', margin: '0 0 0.5rem' }}>
          Free Creator Rate Card Generator
        </h1>
        <p style={{ color: '#666', fontSize: '1rem', margin: 0 }}>
          Not sure what to charge brands? Enter your stats — get suggested pricing + a professional rate card to send brands. No signup.
        </p>
      </div>

      {/* Main */}
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '1.5rem 1.25rem 3rem', display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: '1.5rem', alignItems: 'start' }}
        className="rct-grid">
        {/* Inputs */}
        <div style={card()}>
          <Field label="Your name / channel">
            <input style={input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Arjun Finance" />
          </Field>
          <Field label="Niche">
            <select style={input} value={niche} onChange={e => setNiche(e.target.value)}>
              {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Primary platform">
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {['YouTube', 'Instagram'].map(p => (
                <button key={p} onClick={() => setPlatform(p)} style={{
                  flex: 1, padding: '0.55rem', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
                  border: `1.5px solid ${platform === p ? ACCENT : '#e0e0e0'}`,
                  background: platform === p ? ACCENT : 'white', color: platform === p ? 'white' : '#666',
                }}>{p}</button>
              ))}
            </div>
          </Field>
          <Field label={platform === 'YouTube' ? 'Subscribers' : 'Followers'}>
            <input style={input} type="number" value={followers} onChange={e => setFollowers(e.target.value)} placeholder="e.g. 50000" />
          </Field>
          <Field label={`Average views per ${platform === 'YouTube' ? 'video' : 'reel'}`}>
            <input style={input} type="number" value={avgViews} onChange={e => setAvgViews(e.target.value)} placeholder="e.g. 20000" />
          </Field>
          <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
            Estimates use 2025 India creator benchmarks (~₹1,265–₹2,109 per 1,000 views), adjusted for your niche. A <b>starting point for negotiation</b> — not a fixed price. Real rates also depend on engagement, exclusivity & usage rights.
          </p>
        </div>

        {/* Preview */}
        <div style={card()}>
          {!hasData ? (
            <div style={{ textAlign: 'center', color: '#aaa', padding: '2.5rem 1rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>💰</div>
              <div style={{ marginBottom: '1.25rem' }}>
                Your rate card builds here <b>automatically</b> as you fill the form — no submit button.<br />
                Enter your <b style={{ color: ACCENT }}>average views</b> to start.
              </div>
              <button disabled style={{ ...btn(ACCENT), width: '100%', opacity: 0.4, cursor: 'not-allowed' }}>⬇️ Download / Print Rate Card</button>
            </div>
          ) : (
            <div>
              <div style={{ borderBottom: `3px solid ${ACCENT}`, paddingBottom: '0.75rem', marginBottom: '0.5rem' }}>
                <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.25rem' }}>{name || 'Your'} Rate Card</div>
                <div style={{ color: '#888', fontSize: '0.8rem' }}>{niche} • {platform}{followers ? ` • ${Number(followers).toLocaleString('en-IN')} followers` : ''}</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.label}>
                      <td style={{ padding: '0.6rem 0.25rem', borderBottom: '1px solid #eee' }}>
                        <b>{r.label}</b><div style={{ fontSize: '0.72rem', color: '#999' }}>{r.hint}</div>
                      </td>
                      <td style={{ padding: '0.6rem 0.25rem', borderBottom: '1px solid #eee', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700, color: ACCENT }}>
                        {fmt(r.range.low)} – {fmt(r.range.high)}
                      </td>
                    </tr>
                  ))}
                  {packages.map(p => (
                    <tr key={p.name}>
                      <td style={{ padding: '0.6rem 0.25rem', borderBottom: '1px solid #eee' }}>
                        <b>{p.name}</b><div style={{ fontSize: '0.72rem', color: '#999' }}>{p.desc}</div>
                      </td>
                      <td style={{ padding: '0.6rem 0.25rem', borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 700, color: ACCENT }}>{fmt(p.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.75rem' }}>Indicative ranges (2025 India benchmarks), exclusive of 18% GST. Final rate depends on engagement, exclusivity & usage rights.</p>
              <button onClick={downloadCard} style={{ ...btn(ACCENT), width: '100%', marginTop: '0.5rem' }}>⬇️ Download / Print Rate Card</button>
            </div>
          )}
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: INK, color: 'white', padding: '2.5rem 1.25rem', textAlign: 'center' }}>
        <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.5rem', margin: '0 0 0.5rem' }}>
          Your rate card is just the start.
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.8)', maxWidth: 560, margin: '0 auto 1.25rem', fontSize: '0.95rem' }}>
          CreatorKhata manages your entire brand-deal flow — pitch, contract, invoice, and payment tracking — in one place. Stop leaving money on the table.
        </p>
        <a href={SIGNUP_URL} style={{ ...btn('white'), color: INK, textDecoration: 'none', fontSize: '1rem', padding: '0.8rem 1.6rem' }}>
          Try CreatorKhata free — PRO included till August →
        </a>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', fontSize: '0.75rem' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT, display: 'inline-block', marginRight: 6 }} />
        Made with CreatorKhata • <a href="/" style={{ color: '#94a3b8' }}>creatorkhata.com</a>
      </div>

      <style>{`@media (max-width: 760px){ .rct-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 700, color: '#444', marginBottom: '0.9rem' }}>
      {label}
      {children}
    </label>
  );
}

const input = { padding: '0.6rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', background: 'white', width: '100%', boxSizing: 'border-box' };
const card = () => ({ background: 'white', borderRadius: 16, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' });
const btn = (bg) => ({ background: bg, color: 'white', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' });
